<template>
  <div>
    <v-dialog v-model="value" width="650" persistent>
      <v-form v-model="valid" ref="form">
        <v-card class="w-100 rounded-lg">
          <v-card-title>{{ dialogTitle }}</v-card-title>
          <v-card-text>
            <v-text-field
              v-model="form.name"
              :label="$t('spedilink.groups.enterName')"
              outlined
              dense
              class="mb-2"
              :rules="requiredRules"
              :disabled="isFieldDisabled"
            />
            <v-textarea
              v-model="form.description"
              :label="$t('spedilink.groups.enterDescription')"
              outlined
              rows="2"
              class="mb-4"
              :disabled="isFieldDisabled"
            />

            <v-checkbox
              v-if="showCompanyShareCheckbox"
              v-model="form.companyShare"
              :label="$t('spedilink.groups.companyShare')"
              hide-details
              class="mt-0 mb-4"
              :disabled="isReadOnly"
            />

            <div class="text-subtitle-2 mb-2">{{ $t('spedilink.groups.selectPersons') }}</div>

            <v-text-field
              v-if="!isFieldDisabled"
              v-model="personSearch"
              prepend-inner-icon="$magnify"
              :placeholder="$t('spedilink.groups.searchPersons')"
              outlined
              dense
              clearable
              hide-details
              class="mb-2"
              :disabled="isFieldDisabled"
            />

            <v-card outlined class="persons-list" :class="{ 'persons-list--readonly': isFieldDisabled }">
              <v-progress-linear v-if="loadingPersons" indeterminate color="primary" />
              <v-list dense class="py-0">
                <template v-if="filteredPersons.length === 0">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title class="grey--text text-caption">
                        {{ $t('spedilink.groups.noPersonsFound') }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-else>
                  <v-list-item
                    v-for="person in filteredPersons"
                    :key="person.id"
                    v-on="isFieldDisabled ? {} : { click: () => !isFieldDisabled && togglePerson(person.id) }"
                    dense
                  >
                    <v-list-item-action v-if="!isFieldDisabled" class="mr-3 my-0">
                      <v-checkbox
                        :input-value="form.userIds.includes(person.id)"
                        color="primary"
                        dense
                        hide-details
                        :disabled="isFieldDisabled"
                        @click.stop="!isFieldDisabled && togglePerson(person.id)"
                      />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>{{ person.firstName }} {{ person.lastName }}</v-list-item-title>
                      <v-list-item-subtitle>{{ person.email }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-list>
            </v-card>

            <div v-if="form.userIds.length > 0" class="mt-2 text-caption grey--text">
              {{ $t('spedilink.groups.selectedCount', { count: form.userIds.length }) }}
            </div>
          </v-card-text>
          <v-card-actions class="d-flex justify-end px-4 pb-4">
            <v-btn outlined :disabled="loading" @click="close()">
              {{ $t('common.cancel') }}
            </v-btn>
            <v-btn
              v-if="!isReadOnly"
              color="primary"
              class="ml-2"
              :loading="loading"
              :disabled="loading"
              depressed
              @click="save()"
            >
              {{ $t('common.save') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <Confirm ref="confirm" />
  </div>
</template>

<script>
import Service from '@/services'
import Confirm from '@/components/dialogs/Confirm'

export default {
  name: 'EditSpediLinkGroupDialog',
  components: {
    Confirm
  },
  props: {
    value: {
      type: Boolean,
      required: true
    },
    group: {
      type: Object,
      default: () => ({})
    },
    mode: {
      type: String,
      default: 'edit',
      validator: (value) => ['edit', 'manageSharing', 'view'].includes(value)
    }
  },
  data () {
    return {
      valid: true,
      loading: false,
      loadingPersons: false,
      persons: [],
      personSearch: '',
      initialCompanyShare: false,
      form: {
        id: null,
        name: '',
        description: '',
        userIds: [],
        companyShare: false
      },
      requiredRules: [
        (v) => !!v || this.$t('validation.fieldRequired')
      ]
    }
  },
  computed: {
    isReadOnly () {
      return this.mode === 'view'
    },
    isFieldDisabled () {
      return this.mode === 'view' || this.mode === 'manageSharing'
    },
    isManageSharingMode () {
      return this.mode === 'manageSharing'
    },
    showCompanyShareCheckbox () {
      return this.mode === 'edit' || this.mode === 'manageSharing'
    },
    dialogTitle () {
      if (this.mode === 'manageSharing') {
        return this.$t('spedilink.groups.manageSharingTitle')
      }
      if (this.mode === 'view') {
        return this.$t('spedilink.groups.viewTitle')
      }
      return this.$t('spedilink.groups.editTitle')
    },
    filteredPersons () {
      if (!this.personSearch) return this.persons
      const search = this.personSearch.toLowerCase()
      return this.persons.filter(p =>
        p.firstName.toLowerCase().includes(search) ||
        p.lastName.toLowerCase().includes(search) ||
        p.email.toLowerCase().includes(search)
      )
    }
  },
  watch: {
    value (newVal) {
      if (newVal) {
        this.initForm()
        this.loadPersons()
      } else {
        this.resetForm()
      }
    }
  },
  methods: {
    initForm () {
      this.form = {
        id: this.group.id,
        name: this.group.name || '',
        description: this.group.description || '',
        userIds: [...(this.group.userIds || [])],
        companyShare: this.group.companyShare || false
      }
      this.initialCompanyShare = this.group.companyShare || false
      this.personSearch = ''
    },
    async loadPersons () {
      this.loadingPersons = true
      try {
        const { data } = await Service.get('spedilink/SpediLinkUsers/GetList', {
          page: 1,
          pageSize: 1000
        })
        this.persons = data.results
      } catch (e) {
      } finally {
        this.loadingPersons = false
      }
    },
    togglePerson (personId) {
      const index = this.form.userIds.indexOf(personId)
      if (index === -1) {
        this.form.userIds.push(personId)
      } else {
        this.form.userIds.splice(index, 1)
      }
    },
    async confirmUnshareIfNeeded () {
      if (!this.initialCompanyShare || this.form.companyShare) {
        return true
      }
      const confirmed = await this.$refs.confirm.open(
        this.$t('spedilink.groups.unshareConfirmTitle'),
        this.$t('spedilink.groups.unshareConfirmMessage', { name: this.form.name })
      )
      return confirmed
    },
    close () {
      this.$emit('close')
      this.resetForm()
    },
    resetForm () {
      this.form = { id: null, name: '', description: '', userIds: [], companyShare: false }
      this.initialCompanyShare = false
      this.personSearch = ''
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
    async save () {
      this.$refs.form.validate()
      if (!this.valid) return

      const confirmed = await this.confirmUnshareIfNeeded()
      if (!confirmed) return

      this.loading = true
      try {
        if (this.isManageSharingMode) {
          if (!this.form.companyShare) {
            await Service.put('spedilink/SpediLinkGroups/unshare', { id: this.form.id })
          } else {
            this.close()
            return
          }
        } else {
          await Service.put('spedilink/SpediLinkGroups/Update', {
            id: this.form.id,
            name: this.form.name,
            description: this.form.description || undefined,
            userIds: this.form.userIds,
            companyShare: this.form.companyShare
          })
        }
        await this.$store.dispatch('setSuccessNotification', this.$t('common.confirmOperation'))
        this.$emit('group-edited')
        this.resetForm()
      } catch (e) {
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.persons-list {
  max-height: 240px;
  overflow-y: auto;
}

.persons-list--readonly {
  opacity: 0.6;
}

.persons-list--readonly .v-list-item {
  cursor: default;
}
</style>
